import {
  EventProvider,
  EVENTS,
  getProvider,
  ProviderEvents,
} from '@ankr.com/provider';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import { getProviderManager } from 'modules/core';
import { api as providerApi, cacheTags, RootState } from 'modules/store';

import { connect } from './actions/connect';
import { disconnect } from './actions/disconnect';
import { updateAccountAddress } from './actions/updateAccountAddress';
import { updateConnectedNetwork } from './actions/updateConnectedNetwork';

export const providerEventsMiddleware = createListenerMiddleware();

providerEventsMiddleware.startListening({
  predicate: action => {
    return !!connect.matchFulfilled(action);
  },
  effect: async (action, api) => {
    api.cancelActiveListeners();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { dispatch, getState } = api;

    const instance = await getProviderManager();
    const provider = await instance.getETHWriteProvider();
    const web3 = provider.getWeb3();

    const eventProvider: EventProvider | null = getProvider(
      web3?.currentProvider,
    );

    if (!eventProvider) {
      return;
    }

    EVENTS.forEach((eventName: ProviderEvents): void => {
      eventProvider?.removeAllListeners(eventName);
    });

    eventProvider.on(ProviderEvents.AccountsChanged, data => {
      const account = data?.[0];
      const { walletId } = (getState() as RootState).auth;
      if (!account) {
        const result = dispatch(disconnect.initiate());
        result.unsubscribe();
        return;
      }

      if (walletId) {
        void dispatch(
          updateAccountAddress.initiate({
            walletId,
            address: account,
          }),
        );
      }

      dispatch(
        providerApi.util.invalidateTags([
          cacheTags.account,
          cacheTags.balance,
          cacheTags.taskData,
          cacheTags.chainData,
        ]),
      );
    });

    eventProvider.on(ProviderEvents.ChainChanged, (chainId: string) => {
      const selectedChainId = Number.parseInt(chainId, 16);
      const { walletId } = (getState() as RootState).auth;

      if (walletId) {
        void dispatch(
          updateConnectedNetwork.initiate({
            walletId,
            chainId: selectedChainId,
          }),
        );
      }

      dispatch(
        providerApi.util.invalidateTags([
          cacheTags.account,
          cacheTags.balance,
          cacheTags.taskData,
          cacheTags.chainData,
        ]),
      );
    });

    eventProvider.on(ProviderEvents.Disconnect, data => {
      if (data instanceof Array && data.length === 0) {
        const result = dispatch(disconnect.initiate());
        result.unsubscribe();
      }
    });
  },
});
